import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import api from '../../api';
import { useAdmin } from '../../context/AdminContext';

const blank = { type: 'offer', importance: 'info', title: '', message: '', isActive: true };

const importanceOptions = [
  { value: 'info',   label: 'Info',   color: '#3F72AF', desc: 'General information' },
  { value: 'low',    label: 'Low',    color: '#4c7a52', desc: 'Minor update' },
  { value: 'medium', label: 'Medium', color: '#e07b1a', desc: 'Notable update' },
  { value: 'high',   label: 'Urgent', color: '#c0392b', desc: 'Visa changes, disruptions' }
];

const importanceColors = {
  info:   '#3F72AF',
  low:    '#4c7a52',
  medium: '#e07b1a',
  high:   '#c0392b'
};

export default function AdminAlerts() {
  const [alerts, setAlerts]     = useState([]);
  const [form, setForm]         = useState(blank);
  const [editingId, setEditingId] = useState(null);
  const { isSuperAdmin } = useAdmin();

  const load = function() {
    api.get('/alerts/all').then(function(res) { setAlerts(res.data); }).catch(function() {});
  };
  useEffect(function() { load(); }, []);

  const handleSubmit = async function(e) {
    e.preventDefault();
    if (editingId) {
      await api.put('/alerts/' + editingId, form);
    } else {
      await api.post('/alerts', form);
    }
    setForm(blank);
    setEditingId(null);
    load();
  };

  const startEdit = function(a) {
    setForm({
      type: a.type,
      importance: a.importance || 'info',
      title: a.title,
      message: a.message,
      isActive: a.isActive
    });
    setEditingId(a._id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleActive = async function(a) {
    await api.put('/alerts/' + a._id, { isActive: !a.isActive });
    load();
  };

  const remove = async function(id) {
    if (!window.confirm('Delete this alert?')) return;
    await api.delete('/alerts/' + id);
    load();
  };

  return (
    <AdminLayout>
      <h1>Alerts &amp; Offers</h1>
      <p style={{ opacity: 0.7, marginBottom: 24 }}>
        <strong>Offers</strong> appear in the rotating banner at the top of the homepage.
        <strong> Travel Alerts</strong> appear in the Travel Alerts section with importance-colored strips and dots.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24 }}>

        {/* FORM */}
        <form onSubmit={handleSubmit} className="card" style={{ padding: 24 }}>
          <h3>{editingId ? 'Edit' : 'New'} Alert</h3>

          <div className="form-field" style={{ marginBottom: 14 }}>
            <label>Type</label>
            <select value={form.type} onChange={function(e) { setForm({ ...form, type: e.target.value }); }}>
              <option value="offer">Offer / Promotion</option>
              <option value="travel-alert">Travel Alert</option>
            </select>
          </div>

          {/* Importance only shown for travel alerts */}
          {form.type === 'travel-alert' && (
            <div className="form-field" style={{ marginBottom: 14 }}>
              <label>Importance Level</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 6 }}>
                {importanceOptions.map(function(opt) {
                  var selected = form.importance === opt.value;
                  return (
                    <button
                      type="button"
                      key={opt.value}
                      onClick={function() { setForm({ ...form, importance: opt.value }); }}
                      style={{
                        padding: '10px 12px',
                        borderRadius: 8,
                        border: '2px solid ' + (selected ? opt.color : 'var(--bj-sand)'),
                        background: selected ? opt.color + '18' : '#fff',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'all 0.2s'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <span style={{
                          width: 10, height: 10, borderRadius: '50%',
                          background: opt.color, flexShrink: 0
                        }} />
                        <strong style={{ fontSize: '0.85rem', color: opt.color }}>{opt.label}</strong>
                      </div>
                      <p style={{ margin: '3px 0 0', fontSize: '0.75rem', opacity: 0.65 }}>{opt.desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="form-field" style={{ marginBottom: 14 }}>
            <label>Title</label>
            <input required value={form.title} onChange={function(e) { setForm({ ...form, title: e.target.value }); }} />
          </div>

          <div className="form-field" style={{ marginBottom: 14 }}>
            <label>Message</label>
            <textarea rows={3} required value={form.message} onChange={function(e) { setForm({ ...form, message: e.target.value }); }} />
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, fontWeight: 600, fontSize: '0.9rem' }}>
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={function(e) { setForm({ ...form, isActive: e.target.checked }); }}
            />
            Active (visible on site)
          </label>

          <button className="btn btn-primary" style={{ width: '100%' }}>
            {editingId ? 'Update' : 'Publish'}
          </button>
          {editingId && (
            <button
              type="button"
              className="btn btn-outline"
              style={{ width: '100%', marginTop: 8 }}
              onClick={function() { setForm(blank); setEditingId(null); }}
            >
              Cancel
            </button>
          )}
        </form>

        {/* LIST */}
        <div className="card" style={{ padding: 20 }}>
          <h3>All Alerts</h3>
          {alerts.length === 0 && <p>No alerts yet.</p>}
          {alerts.map(function(a) {
            var color = a.type === 'travel-alert'
              ? (importanceColors[a.importance] || importanceColors.info)
              : 'var(--bj-clay)';
            return (
              <div key={a._id} style={{
                display: 'flex', alignItems: 'flex-start', gap: 12,
                padding: '12px 0', borderBottom: '1px solid var(--bj-sand)',
                opacity: a.isActive ? 1 : 0.5
              }}>
                <div style={{
                  width: 4, borderRadius: 4, alignSelf: 'stretch',
                  background: color, flexShrink: 0, minHeight: 36
                }} />

                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 3 }}>
                    <span style={{
                      fontSize: '0.68rem', fontWeight: 700,
                      letterSpacing: '0.07em', textTransform: 'uppercase',
                      background: color + '1a', color: color,
                      padding: '2px 8px', borderRadius: 20
                    }}>
                      {a.type === 'travel-alert' ? (a.importance || 'info') : 'offer'}
                    </span>
                    {!a.isActive && (
                      <span style={{ fontSize: '0.72rem', color: '#c0392b', fontWeight: 700 }}>Inactive</span>
                    )}
                  </div>
                  <strong style={{ fontSize: '0.9rem' }}>{a.title}</strong>
                  <p style={{ margin: '2px 0 0', fontSize: '0.82rem', opacity: 0.7 }}>{a.message}</p>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 5, flexShrink: 0 }}>
                  <button className="btn btn-outline" style={{ padding: '4px 10px', fontSize: '0.8rem' }} onClick={function() { startEdit(a); }}>Edit</button>
                  <button className="btn btn-outline" style={{ padding: '4px 10px', fontSize: '0.8rem' }} onClick={function() { toggleActive(a); }}>
                    {a.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                  {isSuperAdmin() && (<button className="btn btn-outline" style={{ padding: '4px 10px', fontSize: '0.8rem' }} onClick={function() { remove(a._id); }}>Delete</button>)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </AdminLayout>
  );
}